/**
 * Settings dialog for an existing connection — design_handoff_integrations_page
 * frames 20-22. Replaces the legacy EditIntegrationModal/`/integration/:id`.
 * Three tabs (Calendars/Sync/Scopes) share one Modal; each tab's own draft
 * state lives here (not in the tab components) so the shared footer — whose
 * buttons differ per tab — can be assembled in one place.
 */
import React, { useCallback, useEffect, useState } from "react";
import Modal from "../../../theme/untitled/Modal/Modal";
import Tabs from "../../../theme/untitled/Tabs/Tabs";
import Button from "../../../theme/untitled/Button/Button";
import ProviderSignInButton from "../../IntegrationConnectPage/ProviderSignInButton";
import { SCOPE_DEFINITIONS } from "../../IntegrationConnectPage/scopeDefinitions";
import { AvailableIntegrations } from "../../../consts/integrations";
import { SIGN_IN_PROVIDER } from "../../../../../consts";
import ApiError from "../../../component/ApiError";
import { useIntegrationStore } from "../../../../../hooks/redux/integration";
import { openAuthWindow } from "../../../consts/pco";
import { getSourceName } from "../../DashboardPage/CalendarSourcesWidget/utils";
import CalendarsTab from "./CalendarsTab";
import SyncTab from "./SyncTab";
import ScopesTab from "./ScopesTab";
import styles from "./IntegrationSettingsDialog.module.scss";

const TAB_ITEMS = [
  { key: "calendars", label: "Calendars" },
  { key: "sync", label: "Sync" },
  { key: "scopes", label: "Scopes" },
];

const buildCalendarSelection = (integration) =>
  (integration?.settings?.calendars || []).reduce(
    (acc, c) => ({ ...acc, [c.id]: Boolean(c.sync) }),
    {}
  );

const buildScopeSelection = (integration) => {
  const definitions = SCOPE_DEFINITIONS[integration?.type] || {};
  return Object.keys(definitions).reduce((acc, name) => {
    const scope = (integration.integration_scopes || []).find((s) => s.name === name);
    acc[name] = definitions[name].mandatory || Boolean(scope?.available);
    return acc;
  }, {});
};

// cake-app/src/Form/Integrations/EditForm.php reads integration_scopes
// unconditionally (hasScopesChanged(), before the EDITABLE_FIELDS
// intersection even runs) and crashes with a TypeError if it's missing —
// PATCH-looking semantics elsewhere in that same form, but this one field
// must always be resent even when untouched. Matches the shape
// IntegrationsPage.js's onToggleAutoSync already sends for the same reason.
// `type` must always be sent alongside it: EditForm.php's regenerate-auth-url
// switch (addGoogle()/addOutlook()) reads $data['type'] whenever the
// integration is new, scopes changed, OR status is already re-auth-required/
// permissions-revoked — the last case can trip on a plain Calendars/Sync save
// with no scope change at all, so `type` can't be conditional on that either.
const buildBasePayload = (integration) => ({
  type: integration.type,
  available: integration.available,
  name: integration.name,
  pull_events_ahead: integration.pull_events_ahead,
  integration_scopes: (integration.integration_scopes || []).map((scope) => ({
    uuid: scope.uuid,
    name: scope.name,
    available: scope.available,
  })),
});

/**
 * @param {object=} integration - null/undefined closes the dialog
 * @param {"calendars"|"sync"|"scopes"="calendars"} initialTab
 * @param {function} onClose
 * @param {function} onFullResync
 * @param {function} onDisconnect
 */
const IntegrationSettingsDialog = ({
  integration,
  initialTab = "calendars",
  onClose,
  onFullResync,
  onDisconnect,
}) => {
  const {
    integration: integrationState,
    updateIntegration,
    setIntegrationModel,
    syncIntegration,
  } = useIntegrationStore();

  const [activeTab, setActiveTab] = useState(initialTab);
  const [calendarSelection, setCalendarSelection] = useState({});
  const [autoSync, setAutoSync] = useState(false);
  const [lookback, setLookback] = useState("3m");
  const [scopeSelection, setScopeSelection] = useState({});

  // Re-seed every draft whenever the dialog opens for a (possibly
  // different) integration — not per-tab, so switching tabs never loses
  // edits made on another tab before Save. This singular `integration`
  // redux slice is shared app-wide (also used by the connect flow) — reset
  // it here too, otherwise a stale updateSuccess:true left over from
  // elsewhere in the session would fire the close effect below on mount,
  // before the user ever clicks Save.
  useEffect(() => {
    if (!integration) return;
    setIntegrationModel(null);
    setActiveTab(initialTab);
    setCalendarSelection(buildCalendarSelection(integration));
    setAutoSync(Boolean(Number(integration.is_auto_sync)));
    setScopeSelection(buildScopeSelection(integration));
  }, [integration?.uuid]);

  // Guards the close-on-success effect below against a stale
  // updateSuccess:true left over from unrelated earlier use of this same
  // shared redux slice (e.g. the connect flow, still true from before this
  // dialog ever opened) — only a Save click made *in this dialog* sets it,
  // so the effect can't fire from state it didn't cause.
  const [saving, setSaving] = useState(false);
  const [reauthPending, setReauthPending] = useState(false);
  const [resyncPending, setResyncPending] = useState(false);

  useEffect(() => {
    if (!saving || !integrationState.updateSuccess) return;
    setSaving(false);

    if (reauthPending && integrationState.model?.auth_url) {
      setReauthPending(false);
      openAuthWindow(integrationState.model.auth_url);
      return;
    }

    // Saving which calendars sync only changes what future syncs pull —
    // without kicking one off now, a newly-ticked calendar's events sit
    // empty until the next auto-sync (or forever, if auto-sync is off).
    if (resyncPending) {
      setResyncPending(false);
      syncIntegration(integration.uuid, false);
    }

    onClose();
  }, [integrationState.updateSuccess]);

  const onCalendarChange = useCallback(
    (id, checked) => setCalendarSelection((prev) => ({ ...prev, [id]: checked })),
    []
  );
  const onCalendarSelectAll = useCallback(
    (checked) => {
      const calendars = integration?.settings?.calendars || [];
      setCalendarSelection(calendars.reduce((acc, c) => ({ ...acc, [c.id]: checked }), {}));
    },
    [integration]
  );

  const onScopeChange = useCallback(
    (name, checked) => setScopeSelection((prev) => ({ ...prev, [name]: checked })),
    []
  );

  const onSaveCalendars = useCallback(() => {
    const calendars = (integration.settings?.calendars || []).map((c) => ({
      ...c,
      sync: Boolean(calendarSelection[c.id]),
    }));
    setSaving(true);
    setResyncPending(true);
    updateIntegration(integration.uuid, {
      ...buildBasePayload(integration),
      is_auto_sync: integration.is_auto_sync,
      settings: { ...integration.settings, calendars },
    });
  }, [integration, calendarSelection, updateIntegration]);

  const onSaveSync = useCallback(() => {
    setSaving(true);
    updateIntegration(integration.uuid, {
      ...buildBasePayload(integration),
      is_auto_sync: autoSync ? 1 : 0,
    });
  }, [integration, autoSync, updateIntegration]);

  const onSignInScopes = useCallback(() => {
    const definitions = SCOPE_DEFINITIONS[integration.type];
    const integration_scopes = Object.keys(definitions).map((name) => ({
      name,
      available: definitions[name].mandatory || scopeSelection[name] ? 1 : 0,
    }));
    setSaving(true);
    setReauthPending(true);
    updateIntegration(integration.uuid, {
      ...buildBasePayload(integration),
      integration_scopes,
    });
  }, [integration, scopeSelection, updateIntegration]);

  if (!integration) {
    return null;
  }

  const meta = AvailableIntegrations.find((a) => a.type === integration.type);
  const selectedCalendarCount = Object.values(calendarSelection).filter(Boolean).length;
  const totalCalendarCount = (integration.settings?.calendars || []).length;
  const signInProvider = SIGN_IN_PROVIDER[integration.type];

  let tabBody;
  let footerRight;

  if (activeTab === "calendars") {
    tabBody = (
      <CalendarsTab
        integration={integration}
        selected={calendarSelection}
        onChange={onCalendarChange}
        onSelectAll={onCalendarSelectAll}
      />
    );
    footerRight = (
      <>
        <span className={styles.footerNote}>
          {selectedCalendarCount} of {totalCalendarCount} selected
        </span>
        <Button hierarchy="secondaryGray" onClick={onClose}>
          Cancel
        </Button>
        <Button hierarchy="primary" onClick={onSaveCalendars} disabled={integrationState.update}>
          Save changes
        </Button>
      </>
    );
  } else if (activeTab === "sync") {
    tabBody = (
      <SyncTab
        integration={integration}
        autoSync={autoSync}
        onAutoSyncChange={setAutoSync}
        lookback={lookback}
        onLookbackChange={setLookback}
        onFullResync={onFullResync}
        onDisconnect={onDisconnect}
      />
    );
    footerRight = (
      <>
        <Button hierarchy="secondaryGray" onClick={onClose}>
          Cancel
        </Button>
        <Button hierarchy="primary" onClick={onSaveSync} disabled={integrationState.update}>
          Save changes
        </Button>
      </>
    );
  } else {
    tabBody = (
      <ScopesTab integration={integration} scopes={scopeSelection} onScopeChange={onScopeChange} />
    );
    footerRight = (
      <>
        <Button hierarchy="secondaryGray" onClick={onClose}>
          Cancel
        </Button>
        <ProviderSignInButton
          provider={signInProvider}
          onClick={onSignInScopes}
          disabled={integrationState.update}
        />
      </>
    );
  }

  return (
    <Modal open={Boolean(integration)} onClose={onClose} size="settings">
      <div className={styles.root}>
        <div className={styles.head}>
          <span className={styles.mark}>
            {meta ? <img src={`/img/integrations_icons/${meta.icon}`} alt="" /> : null}
          </span>
          <div className={styles.headText}>
            <b>{getSourceName(integration)}</b>
            <span className={styles.account}>{integration.username}</span>
          </div>
        </div>

        <Tabs
          variant="segmented"
          items={TAB_ITEMS}
          value={activeTab}
          onChange={setActiveTab}
          className={styles.tabs}
        />

        <div className={styles.body}>
          <ApiError
            error={integrationState.updateError}
            defaultErrorMessage="Could not save your changes. Please try again."
          />
          {tabBody}
        </div>

        <div className={styles.footer}>{footerRight}</div>
      </div>
    </Modal>
  );
};

export default IntegrationSettingsDialog;
